import { CircularProgress, Modal } from '@mui/material';
import { alpha } from '@mui/material/styles';
import styled from 'styled-components';

import { MessageContainer } from '../PageLayout/index.styles';

/** Width of the inner sidebar, matching the main drawer's expanded width. */
const SIDEBAR_WIDTH = 240;

const SURFACE_WIDTH = 1040;
const SURFACE_HEIGHT = 720;

// The page behind is left legible but plainly out of reach.
const StyledModal = styled(Modal)`
  && {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${({ theme }) => theme.spacing(2)};

    & .MuiBackdrop-root {
      backdrop-filter: blur(8px);
      background-color: ${({ theme }) => alpha(theme.palette.common.black, 0.35)};
    }
  }
`;

const Surface = styled.div`
  display: flex;
  flex-direction: column;
  width: min(${SURFACE_WIDTH}px, 100%);
  /* fallback for browsers without dvh support */
  height: min(${SURFACE_HEIGHT}px, calc(100vh - ${({ theme }) => theme.spacing(4)}));
  height: min(${SURFACE_HEIGHT}px, calc(100dvh - ${({ theme }) => theme.spacing(4)}));
  overflow: hidden;
  border-radius: ${({ theme }) => theme.shape.borderRadius * 2}px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  background-color: ${({ theme }) => theme.palette.background.canvas};
  outline: none;
`;

const Header = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
`;

const Body = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(3)};
  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing(3)};
  }
`;

// Sticky inside the modal's own scroller, so the sections stay in reach however
// long the page beside them runs. The fill is the main drawer's, so the rail
// reads as navigation beside the page rather than as part of it.
const Sidebar = styled.nav`
  flex-shrink: 0;
  width: ${SIDEBAR_WIDTH}px;
  position: sticky;
  /* The body's own inset, so the rail keeps it once the page beside it scrolls */
  top: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.shape.borderRadius * 2}px;
  background-color: ${({ theme }) => theme.palette.background.default};
`;

const Content = styled.div`
  flex: 1 1 auto;
  min-width: 0;

  /* The page messages fill the viewport, which is taller than the panel holding
     them: here they take only the room they need. */
  ${MessageContainer} {
    height: auto;
    padding: ${({ theme }) => theme.spacing(4)} 0;
  }
`;

const Loading = styled(CircularProgress)`
  && {
    display: block;
    margin: ${({ theme }) => theme.spacing(4)} auto;
  }
`;

// Traces MenuList's own rows: the same inset and the same spacing between them,
// so the sections land in the space their placeholders held.
const SidebarRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  margin: ${({ theme }) => theme.spacing(1)};
`;

export { Body, Content, Header, Loading, Sidebar, SidebarRows, StyledModal, Surface };
